import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { cloudApi } from '../services/api';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

interface Props {
  provider: 'google' | 'dropbox' | 'onedrive';
}

export default function OAuthCallback({ provider }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addNotification, setCloudConnections } = useStore();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage('Authorization was denied');
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorMessage('No authorization code received');
        return;
      }

      try {
        if (provider === 'google') {
          await cloudApi.connectGoogle(code);
          setCloudConnections({ googleDrive: true });
        } else if (provider === 'dropbox') {
          await cloudApi.connectDropbox(code);
          setCloudConnections({ dropbox: true });
        } else if (provider === 'onedrive') {
          await cloudApi.connectOneDrive(code);
          setCloudConnections({ oneDrive: true });
        }

        setStatus('success');
        addNotification(`Connected to ${getProviderName(provider)}`, 'success');

        // Redirect after short delay
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } catch (err: any) {
        setStatus('error');
        setErrorMessage(err.response?.data?.error || 'Connection failed');
        addNotification('Failed to connect', 'error');
      }
    };

    handleCallback();
  }, [provider, searchParams, navigate, addNotification, setCloudConnections]);

  const getProviderName = (p: string) => {
    switch (p) {
      case 'google':
        return 'Google Drive';
      case 'dropbox':
        return 'Dropbox';
      case 'onedrive':
        return 'OneDrive';
      default:
        return p;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-accent mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">
              Connecting to {getProviderName(provider)}...
            </h2>
            <p className="text-gray-600 mt-2">Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Connected!</h2>
            <p className="text-gray-600 mt-2">
              Successfully connected to {getProviderName(provider)}. Redirecting...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Connection Failed</h2>
            <p className="text-gray-600 mt-2">{errorMessage}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 btn btn-primary"
            >
              Return to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
